import React from "react";
import NodePorts from "./NodePorts";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Typography } from "@material-ui/core";
import {
  Message,
  Image as ImageIcon,
  MusicNote,
  VideoLibrary,
  Description,
  Timer,
  Layers,
} from "@material-ui/icons";

const ITEM_COLORS = {
  text:     "#2196F3",
  image:    "#4CAF50",
  audio:    "#9C27B0",
  video:    "#FF5722",
  document: "#607D8B",
  interval: "#78909C",
};

const ITEM_ICONS = {
  text:     Message,
  image:    ImageIcon,
  audio:    MusicNote,
  video:    VideoLibrary,
  document: Description,
  interval: Timer,
};

const MAX_VISIBLE_ITEMS = 5;

function getItemPreview(item) {
  switch (item.kind) {
    case "text":
      if (!item.text) return "Sin texto";
      return item.text.length > 34 ? `${item.text.substring(0, 34)}…` : item.text;
    case "image":
      return item.fileName || item.caption || "Imagen";
    case "audio":
      return item.fileName || item.caption || "Audio";
    case "video":
      return item.fileName || item.caption || "Video";
    case "document":
      return item.fileName || item.caption || "Documento";
    case "interval":
      return `Espera ${item.seconds ?? 1}s`;
    default:
      return item.kind;
  }
}

const useStyles = makeStyles((theme) => ({
  nodeContainer: {
    minWidth: 230,
    maxWidth: 310,
    borderRadius: 10,
    background: "linear-gradient(135deg, #1a237e 0%, #4a148c 100%)",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    color: "white",
    position: "relative",
    // SIN overflow hidden: los conectores van 6 px por FUERA del borde y el recorte les quitaba
    // la mitad —solo quedaba una franja de ~5 px donde soltar una flecha—. Cada fila ya recorta
    // su propio texto con puntos suspensivos.
    transition: "opacity 0.2s ease",
    "&:hover": {
      opacity: 0.93,
    },
  },
  nodeHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 1.75),
    borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
  },
  headerIcon: {
    fontSize: 18,
    opacity: 0.9,
    flexShrink: 0,
  },
  nodeLabel: {
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: 0.3,
    color: "white",
    flex: 1,
  },
  countBadge: {
    background: "rgba(255,255,255,0.18)",
    borderRadius: 10,
    padding: "1px 8px",
    fontSize: 11,
    fontWeight: 700,
    color: "white",
    flexShrink: 0,
  },
  itemsList: {
    padding: theme.spacing(0.75, 1),
  },
  itemRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 0.75),
    borderRadius: 4,
    marginBottom: 2,
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    // El recorte vive AQUÍ y no en el contenedor (que tiene los conectores): una URL o un texto
    // largo no puede ensanchar el nodo.
    overflow: "hidden",
    minWidth: 0,
  },
  itemIcon: {
    fontSize: 14,
    flexShrink: 0,
  },
  itemText: {
    fontSize: 11,
    opacity: 0.88,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
    minWidth: 0,
    color: "white",
  },
  emptyState: {
    padding: theme.spacing(1.5, 1.75),
    fontSize: 12,
    opacity: 0.5,
    fontStyle: "italic",
    textAlign: "center",
    color: "white",
  },
  moreLabel: {
    padding: theme.spacing(0.25, 0.75, 0.75),
    fontSize: 11,
    opacity: 0.55,
    textAlign: "center",
    color: "white",
  },
  handle: {
    width: 12,
    height: 12,
    background: "white",
    border: "2px solid #4a148c",
    borderRadius: "50%",
  },
}));

const safeParseItems = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.trim().startsWith("[")) {
    try { return JSON.parse(raw); } catch { return []; }
  }
  return [];
};

const ContentNode = ({ data, selected }) => {
  const classes = useStyles();
  const items = safeParseItems(data?.items);
  const visibleItems = items.slice(0, MAX_VISIBLE_ITEMS);
  const extraCount = items.length - MAX_VISIBLE_ITEMS;

  return (
    <Box
      className={classes.nodeContainer}
      style={{
        border: selected
          ? "2px solid rgba(255, 255, 255, 0.55)"
          : "1px solid rgba(255, 255, 255, 0.15)",
      }}
    >
      {/* Morado claro: con el #4a148c del fondo los conectores eran casi invisibles. */}
      <NodePorts color="#b388ff" />
      <Box className={classes.nodeHeader}>
        <Layers className={classes.headerIcon} />
        <Typography className={classes.nodeLabel}>Bloque de Respuesta</Typography>
        {items.length > 0 && (
          <Typography component="span" className={classes.countBadge}>
            {items.length}
          </Typography>
        )}
      </Box>

      {items.length === 0 ? (
        <Typography className={classes.emptyState}>
          Haz clic para agregar contenido
        </Typography>
      ) : (
        <Box className={classes.itemsList}>
          {visibleItems.map((item, idx) => {
            const Icon = ITEM_ICONS[item.kind] || Message;
            const color = ITEM_COLORS[item.kind] || "#90caf9";
            return (
              <Box key={item.id || idx} className={classes.itemRow}>
                <Icon className={classes.itemIcon} style={{ color }} />
                <Typography className={classes.itemText}>
                  {getItemPreview(item)}
                </Typography>
              </Box>
            );
          })}
          {extraCount > 0 && (
            <Typography className={classes.moreLabel}>
              +{extraCount} elemento{extraCount > 1 ? "s" : ""} más
            </Typography>
          )}
        </Box>
      )}

    </Box>
  );
};

export default ContentNode;
